import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Purchase.css';

const apiBaseURL = (process.env.REACT_APP_API_BASE_URL || 'https://api.vehiclesolution.co').replace(/\/+$/, '');

function Purchase({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    grn_no: '',
    purchase_date: '',
    part_id: '',
    vendor_name: '',
    part_no: '',
    unit_price: '',
    qty: '',
    total: '',
    tx_no: '',
    Part_type: '',
    Sale_Price: '',
    user_id: '',
    Invoice_Date: '',
    IsSend: false
  });

  // Set default date/time
  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setForm(prev => ({
      ...prev,
      purchase_date: now.toISOString().slice(0, 16),
      Invoice_Date: now.toISOString().slice(0, 16)
    }));
  }, []);

  useEffect(() => {
    const fetchNextNumbers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBaseURL}/api/purchase/next-numbers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();

        if (response.ok && data.success) {
          setForm(prev => ({
            ...prev,
            grn_no: data.grn_no || prev.grn_no,
            tx_no: data.tx_no || prev.tx_no
          }));
        }
      } catch (error) {
        console.error('Error fetching purchase numbers:', error);
      }
    };

    fetchNextNumbers();
  }, []);

  // Fetch parts for selection
  useEffect(() => {
    fetchParts();
  }, []);

  useEffect(() => {
    const trimmedVendorSearch = vendorSearchTerm.trim();

    if (!trimmedVendorSearch) {
      setVendors([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchVendors(trimmedVendorSearch);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [vendorSearchTerm]);

  const fetchParts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/parts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setParts(data.parts || []);
      }
    } catch (error) {
      console.error('Error fetching parts:', error);
    }
  };

  // Filter parts for search
  const filteredParts = parts.filter(part =>
    part.Part_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    part.parts_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nonNegativeFields = ['unit_price', 'qty', 'total', 'Sale_Price'];

    if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
      return;
    }

    if (nonNegativeFields.includes(name)) {
      if (value === '') {
        setForm({ ...form, [name]: value });
        return;
      }

      const numericValue = Number(value);
      setForm({ ...form, [name]: numericValue < 0 ? '0' : value });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const fetchVendors = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseURL}/api/vendors?search=${encodeURIComponent(query)}&page=1&pageSize=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setVendors(data.vendors || []);
      }
    } catch (fetchError) {
      console.error('Error fetching vendors:', fetchError);
    }
  };

  const handlePartSelect = (part) => {
    const stockQty = parseFloat(part.Qty ?? part.opening_qty);
    const openingValue = parseFloat(part.opening_value);
    const calculatedUnitPrice = stockQty > 0 && !Number.isNaN(openingValue)
      ? (openingValue / stockQty).toFixed(2)
      : '';

    setSelectedPart(part);
    setForm(prev => ({
      ...prev,
      part_id: part.PartId,
      vendor_name: part.vendor_name || '',
      part_no: part.Part_no,
      Part_type: part.Part_type || '',
      unit_price: calculatedUnitPrice
    }));
    setSearchTerm('');
    setVendorSearchTerm(part.vendor_name || '');
  };

  const handleVendorSelect = (vendor) => {
    setForm(prev => ({
      ...prev,
      vendor_name: vendor.company || ''
    }));
    setVendorSearchTerm(vendor.company || '');
    setVendors([]);
  };

  // Calculate total when qty or unit_price changes
  useEffect(() => {
    if (form.qty && form.unit_price) {
      const total = parseFloat(form.qty) * parseFloat(form.unit_price);
      setForm(prev => ({ ...prev, total: total.toFixed(2) }));
    }
  }, [form.qty, form.unit_price]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const unitPrice = parseFloat(form.unit_price);
    const quantity = parseFloat(form.qty);
    const totalAmount = parseFloat(form.total);
    const salePrice = form.Sale_Price === '' ? 0 : parseFloat(form.Sale_Price);

    if (Number.isNaN(quantity) || quantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }

    if ([unitPrice, totalAmount, salePrice].some((value) => Number.isNaN(value) || value < 0)) {
      setError('Negative values are not allowed for unit price, total, or sale price.');
      return;
    }

    if (salePrice <= unitPrice) {
      setError('Sale price must be greater than unit price.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const formData = {
        ...form,
        part_id: parseInt(form.part_id),
        qty: quantity,
        unit_price: unitPrice,
        total: totalAmount,
        Sale_Price: salePrice,
        user_id: form.user_id ? parseInt(form.user_id) : null
      };

      const response = await fetch(`${apiBaseURL}/api/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Purchase added successfully!');
        navigate('/inventory');
      } else {
        setError(data.message || 'Unknown error');
      }
    } catch (error) {
      setError(error.message);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="purchase-container">
      <h2>Add Purchase Item</h2>
      <form onSubmit={handleSubmit} className="purchase-form">
        <div className="row">
          <div className="col">
            <label>GRN No</label>
            <input 
              name="grn_no" 
              placeholder="GRN Number" 
              value={form.grn_no} 
              disabled
              required
            />
          </div>
          <div className="col">
            <label>Purchase Date</label>
            <input 
              name="purchase_date" 
              type="datetime-local" 
              value={form.purchase_date} 
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label>Part Search</label>
            <input 
              type="text"
              placeholder="Search part by number or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && filteredParts.length > 0 && (
              <div className="part-suggestions">
                {filteredParts.slice(0, 5).map(part => (
                  <div 
                    key={part.PartId} 
                    className="part-suggestion"
                    onClick={() => handlePartSelect(part)}
                  >
                    <strong>{part.Part_no}</strong> - {part.parts_name} - {part.Part_type || '-'}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedPart && (
          <div className="selected-part-info">
            <p><strong>Selected:</strong> {selectedPart.parts_name} ({selectedPart.Part_no})</p>
            <p>
              <strong>Vendor:</strong> {selectedPart.vendor_name || '-'} |{' '}
              <strong>Stock Qty:</strong> {selectedPart.Qty ?? selectedPart.opening_qty ?? 0} | <strong>Opening Value:</strong> {selectedPart.opening_value ?? 0}
            </p>
          </div>
        )}

        <div className="row">
          <div className="col">
            <label>Vendor</label>
            <input
              name="vendor_name"
              placeholder="Vendor"
              value={form.vendor_name}
              onChange={(e) => {
                handleChange(e);
                setVendorSearchTerm(e.target.value);
              }}
            />
            {vendorSearchTerm && vendors.length > 0 && (
              <div className="part-suggestions">
                {vendors.map((vendor) => (
                  <div
                    key={vendor.VendorId}
                    className="part-suggestion"
                    onClick={() => handleVendorSelect(vendor)}
                  >
                    <strong>{vendor.company}</strong>
                    {vendor.phone1 ? ` - ${vendor.phone1}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label>Part No (Auto-filled)</label>
            <input 
              name="part_no" 
              placeholder="Part Number" 
              value={form.part_no} 
              onChange={handleChange}
              disabled
            />
          </div>
          <div className="col">
            <label>Unit Price</label>
            <input 
              name="unit_price" 
              type="number" 
              min="0"
              step="0.01"
              placeholder="Unit Price" 
              value={form.unit_price} 
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label>Quantity</label>
            <input 
              name="qty" 
              type="number" 
              min="0.1"
              step="0.1"
              placeholder="Quantity" 
              value={form.qty} 
              onChange={handleChange}
              required
            />
          </div>
          <div className="col">
            <label>Total</label>
            <input 
              name="total" 
              type="number" 
              min="0"
              step="0.01"
              placeholder="Total" 
              value={form.total} 
              onChange={handleChange}
              disabled
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label>Barcode</label>
            <input 
              name="tx_no" 
              placeholder="Barcode" 
              value={form.tx_no} 
              disabled
            />
          </div>
          <div className="col">
            <label>Sale Price</label>
            <input 
              name="Sale_Price" 
              type="number" 
              min="0"
              step="0.01"
              placeholder="Sale Price" 
              value={form.Sale_Price} 
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label>Part_type</label>
            <select
              name="Part_type"
              value={form.Part_type}
              onChange={handleChange}
              required
            >
              <option value="">Select Part_type</option>
              <option value="Genuine">Genuine</option>
              <option value="Recondition">Recondition</option>
              <option value="Used">Used</option>
              <option value="OEM">OEM</option>
              <option value="Recycled">Recycled</option>
            </select>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="row">
          <div className="col">
            <label>Invoice Date</label>
            <div className="invoice-date-warning">Plz check Invoice Date Carefully then write</div>
            <input 
              name="Invoice_Date" 
              type="datetime-local" 
              value={form.Invoice_Date} 
              onChange={handleChange}
            />
          </div>
          <div className="col">
            <label className="checkbox-label">
              <input 
                name="IsSend" 
                type="checkbox" 
                checked={form.IsSend} 
                onChange={handleChange}
              />
              Is Send
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Purchase'}
          </button>
          <button type="button" onClick={() => navigate('/inventory')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default Purchase;
